import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";

import {
useGetEnglishQuestionsByChapterQuery,
useGetEnglishQuestionsByGroupQuery,
useGetEnglishQuestionsBySubjectQuery,
useDeleteEnglishQuestionMutation,
useToggleEnglishQuestionStatusMutation,
} from "../../slices/englishQuestionApiSlice.js";

import EnglishQuestionHeader from "./components/EnglishQuestionHeader";
import EnglishQuestionFilters from "./components/EnglishQuestionFilters";
import EnglishQuestionTable from "./components/EnglishQuestionTable";
import EnglishQuestionCard from "./components/EnglishQuestionCard";

const EnglishQuestionList = ({
subjectId = "",
chapterId = "",
groupId = "",
}) => {
const [viewMode, setViewMode] = useState("table");
const [search, setSearch] = useState("");
const [difficulty, setDifficulty] = useState("");
const [questionType, setQuestionType] = useState("");
const [status, setStatus] = useState("");
const [currentPage, setCurrentPage] = useState(1);

const pageSize = 20;

// ======================================================
// Determine Active Query
// ======================================================

const queryOptions = {
skip: !subjectId && !chapterId && !groupId,
};

const {
data: subjectData,
isLoading: isSubjectLoading,
isFetching: isSubjectFetching,
error: subjectError,
refetch: refetchSubject,
} = useGetEnglishQuestionsBySubjectQuery(subjectId, {
skip: !subjectId || !!chapterId || !!groupId,
});

const {
data: chapterData,
isLoading: isChapterLoading,
isFetching: isChapterFetching,
error: chapterError,
refetch: refetchChapter,
} = useGetEnglishQuestionsByChapterQuery(chapterId, {
skip: !chapterId || !!groupId,
});

const {
data: groupData,
isLoading: isGroupLoading,
isFetching: isGroupFetching,
error: groupError,
refetch: refetchGroup,
} = useGetEnglishQuestionsByGroupQuery(groupId, {
skip: !groupId,
});

// ======================================================
// Select Active Response
// ======================================================

const activeData = groupId
? groupData
: chapterId
? chapterData
: subjectId
? subjectData
: null;

const isLoading = groupId
? isGroupLoading
: chapterId
? isChapterLoading
: subjectId
? isSubjectLoading
: false;

const isFetching = groupId
? isGroupFetching
: chapterId
? isChapterFetching
: subjectId
? isSubjectFetching
: false;

const error = groupId
? groupError
: chapterId
? chapterError
: subjectId
? subjectError
: null;

const refetch = groupId
? refetchGroup
: chapterId
? refetchChapter
: refetchSubject;

// ======================================================
// Normalize API Response
// ======================================================

const questions = useMemo(() => {
if (Array.isArray(activeData)) {
return activeData;
}


return (
  activeData?.questions ||
  activeData?.data ||
  activeData?.results ||
  []
);


}, [activeData]);

// ======================================================
// Client-Side Filtering
// ======================================================

const filteredQuestions = useMemo(() => {
let result = [...questions];


const normalizedSearch = search.trim().toLowerCase();

if (normalizedSearch) {
  result = result.filter((question) => {
    const questionText =
      question.questionText ||
      question.question ||
      "";

    const explanation =
      question.explanation || "";

    const questionId =
      question._id || "";

    return (
      questionText
        .toLowerCase()
        .includes(normalizedSearch) ||
      explanation
        .toLowerCase()
        .includes(normalizedSearch) ||
      String(questionId)
        .toLowerCase()
        .includes(normalizedSearch)
    );
  });
}

if (difficulty) {
  result = result.filter(
    (question) =>
      question.difficulty === difficulty,
  );
}

if (questionType) {
  result = result.filter(
    (question) =>
      question.questionType === questionType,
  );
}

if (status) {
  result = result.filter((question) => {
    if (status === "active") {
      return question.isActive === true;
    }

    if (status === "inactive") {
      return question.isActive === false;
    }

    return true;
  });
}

return result;


}, [
questions,
search,
difficulty,
questionType,
status,
]);

// ======================================================
// Pagination
// ======================================================

const totalQuestions =
filteredQuestions.length;

const totalPages = Math.max(
1,
Math.ceil(
totalQuestions / pageSize,
),
);

const safeCurrentPage = Math.min(
currentPage,
totalPages,
);

const paginatedQuestions =
filteredQuestions.slice(
(safeCurrentPage - 1) *
pageSize,
safeCurrentPage * pageSize,
);

// ======================================================
// Mutations
// ======================================================

const [
deleteQuestion,
{
isLoading: isDeleting,
},
] = useDeleteEnglishQuestionMutation();

const [
toggleQuestionStatus,
{
isLoading: isTogglingStatus,
},
] = useToggleEnglishQuestionStatusMutation();

// ======================================================
// Handlers
// ======================================================

const handleSearchChange = (
value,
) => {
setSearch(value);
setCurrentPage(1);
};

const handleDifficultyChange = (
value,
) => {
setDifficulty(value);
setCurrentPage(1);
};

const handleQuestionTypeChange = (
value,
) => {
setQuestionType(value);
setCurrentPage(1);
};

const handleStatusChange = (
value,
) => {
setStatus(value);
setCurrentPage(1);
};

const handleClearFilters = () => {
setSearch("");
setDifficulty("");
setQuestionType("");
setStatus("");
setCurrentPage(1);
};

const handleDelete = async (
question,
) => {
const questionId =
question._id;


const confirmed =
  window.confirm(
    "Are you sure you want to delete this English question?",
  );

if (!confirmed) {
  return;
}

try {
  await deleteQuestion(
    questionId,
  ).unwrap();

  await refetch();
} catch (deleteError) {
  console.error(
    "Failed to delete English question:",
    deleteError,
  );

  window.alert(
    deleteError?.data?.message ||
      "Failed to delete English question.",
  );
}


};

const handleToggleStatus =
async (question) => {
try {
await toggleQuestionStatus(
question._id,
).unwrap();


    await refetch();
  } catch (toggleError) {
    console.error(
      "Failed to toggle question status:",
      toggleError,
    );

    window.alert(
      toggleError?.data?.message ||
        "Failed to update question status.",
    );
  }
};


// ======================================================
// Empty Context State
// ======================================================

if (
!subjectId &&
!chapterId &&
!groupId
) {
return ( <div className="min-h-[400px] rounded-xl border border-gray-200 bg-white p-8 shadow-sm"> <div className="flex flex-col items-center justify-center text-center"> <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-blue-50"> <svg
           className="h-7 w-7 text-blue-600"
           fill="none"
           stroke="currentColor"
           viewBox="0 0 24 24"
         > <path
             strokeLinecap="round"
             strokeLinejoin="round"
             strokeWidth={1.8}
             d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
           /> </svg> </div>


      <h2 className="text-lg font-semibold text-gray-900">
        Select a Question Context
      </h2>

      <p className="mt-2 max-w-md text-sm text-gray-500">
        Select a subject, chapter, or
        question group to view the
        available English questions.
      </p>
    </div>
  </div>
);


}

// ======================================================
// Loading State
// ======================================================

if (isLoading) {
return ( <div className="space-y-6"> <EnglishQuestionHeader />


    <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
      <div className="animate-pulse space-y-4">
        <div className="h-5 w-40 rounded bg-gray-200" />

        <div className="h-10 w-full rounded-lg bg-gray-100" />

        <div className="space-y-3">
          {[1, 2, 3, 4, 5].map(
            (item) => (
              <div
                key={item}
                className="h-16 rounded-lg bg-gray-100"
              />
            ),
          )}
        </div>
      </div>
    </div>
  </div>
);


}

// ======================================================
// Error State
// ======================================================

if (error) {
return ( <div className="space-y-6"> <EnglishQuestionHeader />


    <div className="rounded-xl border border-red-200 bg-red-50 p-6">
      <div className="flex items-start gap-3">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-red-100">
          <svg
            className="h-5 w-5 text-red-600"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v4m0 4h.01M10.29 3.86l-7.82 14a2 2 0 001.74 3h15.58a2 2 0 001.74-3l-7.82-14a2 2 0 00-3.42 0z"
            />
          </svg>
        </div>

        <div className="flex-1">
          <h3 className="font-semibold text-red-800">
            Unable to load English questions
          </h3>

          <p className="mt-1 text-sm text-red-700">
            {error?.data?.message ||
              error?.error ||
              "Something went wrong while loading the questions."}
          </p>

          <button
            type="button"
            onClick={() => refetch()}
            className="mt-4 rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-red-700"
          >
            Try Again
          </button>
        </div>
      </div>
    </div>
  </div>
);


}

// ======================================================
// Main Render
// ======================================================

return ( <div className="space-y-6">
{/* Header */}
<EnglishQuestionHeader
totalQuestions={
totalQuestions
}
viewMode={viewMode}
onViewModeChange={
setViewMode
}
/>


  {/* Filters */}
  <EnglishQuestionFilters
    search={search}
    difficulty={difficulty}
    questionType={
      questionType
    }
    status={status}
    onSearchChange={
      handleSearchChange
    }
    onDifficultyChange={
      handleDifficultyChange
    }
    onQuestionTypeChange={
      handleQuestionTypeChange
    }
    onStatusChange={
      handleStatusChange
    }
    onClear={
      handleClearFilters
    }
  />

  {/* Results Header */}
  <div className="flex flex-col gap-3 rounded-xl border border-gray-200 bg-white px-4 py-3 shadow-sm sm:flex-row sm:items-center sm:justify-between">
    <div>
      <p className="text-sm font-medium text-gray-900">
        {totalQuestions} question
        {totalQuestions === 1
          ? ""
          : "s"} found
      </p>

      <p className="mt-0.5 text-xs text-gray-500">
        Showing{" "}
        {totalQuestions === 0
          ? 0
          : (safeCurrentPage -
              1) *
              pageSize +
            1}{" "}
        -{" "}
        {Math.min(
          safeCurrentPage *
            pageSize,
          totalQuestions,
        )}{" "}
        of {totalQuestions}
      </p>
    </div>

    {isFetching && (
      <div className="flex items-center gap-2 text-xs text-blue-600">
        <svg
          className="h-4 w-4 animate-spin"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          />

          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
          />
        </svg>

        Refreshing...
      </div>
    )}
  </div>

  {/* Empty State */}
  {filteredQuestions.length ===
  0 ? (
    <div className="rounded-xl border border-gray-200 bg-white px-6 py-14 text-center shadow-sm">
      <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-gray-100">
        <svg
          className="h-7 w-7 text-gray-400"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.8}
            d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h6l5 5v11a2 2 0 01-2 2z"
          />
        </svg>
      </div>

      <h3 className="mt-4 text-base font-semibold text-gray-900">
        No English questions found
      </h3>

      <p className="mx-auto mt-1 max-w-md text-sm text-gray-500">
        {search ||
        difficulty ||
        questionType ||
        status
          ? "Try changing or clearing your filters."
          : "No English questions have been created for this context yet."}
      </p>

      {(search ||
        difficulty ||
        questionType ||
        status) && (
        <button
          type="button"
          onClick={
            handleClearFilters
          }
          className="mt-4 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
        >
          Clear Filters
        </button>
      )}

      <Link
        to="/english-questions/create"
        className="mt-4 inline-flex items-center rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
      >
        Create Question
      </Link>
    </div>
  ) : viewMode === "table" ? (
    <EnglishQuestionTable
      questions={
        paginatedQuestions
      }
      onDelete={
        handleDelete
      }
      onToggleStatus={
        handleToggleStatus
      }
      deleting={isDeleting}
      togglingStatus={
        isTogglingStatus
      }
    />
  ) : (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
      {paginatedQuestions.map(
        (question) => (
          <EnglishQuestionCard
            key={question._id}
            question={question}
            onDelete={
              handleDelete
            }
            onToggleStatus={
              handleToggleStatus
            }
            deleting={
              isDeleting
            }
            togglingStatus={
              isTogglingStatus
            }
          />
        ),
      )}
    </div>
  )}

  {/* Pagination */}
  {totalPages > 1 && (
    <div className="flex flex-col gap-3 rounded-xl border border-gray-200 bg-white px-4 py-3 shadow-sm sm:flex-row sm:items-center sm:justify-between">
      <p className="text-sm text-gray-500">
        Page {safeCurrentPage} of{" "}
        {totalPages}
      </p>

      <div className="flex items-center gap-1">
        <button
          type="button"
          disabled={
            safeCurrentPage === 1
          }
          onClick={() =>
            setCurrentPage(
              (page) =>
                Math.max(
                  1,
                  page - 1,
                ),
            )
          }
          className="rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
        >
          Previous
        </button>

        {Array.from(
          {
            length: Math.min(
              totalPages,
              5,
            ),
          },
          (_, index) => {
            let page =
              index + 1;

            if (
              totalPages > 5 &&
              safeCurrentPage >
                3
            ) {
              page =
                safeCurrentPage -
                2 +
                index;

              if (
                page >
                totalPages
              ) {
                page =
                  totalPages -
                  4 +
                  index;
              }
            }

            return (
              <button
                key={page}
                type="button"
                onClick={() =>
                  setCurrentPage(
                    page,
                  )
                }
                className={[
                  "min-w-9 rounded-lg px-3 py-2 text-sm font-medium",
                  page ===
                  safeCurrentPage
                    ? "bg-blue-600 text-white"
                    : "text-gray-700 hover:bg-gray-100",
                ].join(" ")}
              >
                {page}
              </button>
            );
          },
        )}

        <button
          type="button"
          disabled={
            safeCurrentPage ===
            totalPages
          }
          onClick={() =>
            setCurrentPage(
              (page) =>
                Math.min(
                  totalPages,
                  page + 1,
                ),
            )
          }
          className="rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
        >
          Next
        </button>
      </div>
    </div>
  )}
</div>


);
};

export default EnglishQuestionList;
